import { faTh } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import React from "react";
import { MapStateToPropsParam } from "react-redux";

import { StrollerProductPhoto } from "@/mk2/apps/strollers/containers/StrollerProductPhoto/StrollerProductPhoto";
import {
    strollerProductPhotoCheckHideThumbs,
    strollerProductPhotoFetchTrigger,
    strollerProductPhotoToggleThumbsTrigger,
} from "@/mk2/apps/strollers/containers/StrollerProductPhoto/StrollerProductPhoto.actions";
import { strollersProductPhotoReducer } from "@/mk2/apps/strollers/containers/StrollerProductPhoto/StrollerProductPhoto.reducers";
import strollersProductPhotoSaga from "@/mk2/apps/strollers/containers/StrollerProductPhoto/StrollerProductPhoto.sagas";
import {
    getStrollerProductPhotoPagesState,
    getStrollerProductPhotoState,
} from "@/mk2/apps/strollers/containers/StrollerProductPhoto/StrollerProductPhoto.selectors";
import {
    strollersPhotosGalleryUrl,
    strollersVariantPhotosGalleryUrl,
} from "@/mk2/apps/strollers/urls";
import { Btn, BtnType } from "@/mk2/components/Btn";
import { AppName } from "@/mk2/containers/Application/Application";
import { MapDispatchToPropsObject } from "@/mk2/helpers/types";
import { url } from "@/mk2/helpers/urls";
import Page, { HalfpageProps, PageRouteProps, RightContentSide } from "@/mk2/pages/Page";
import { AppState } from "@/mk2/reducers";
import { LoadingState } from "@/mk2/reducers/loadingState";
import {
    PhotoDetailSchema,
    StrollerEntity,
    StrollerPhoto,
    StrollerSchema,
    StrollerVariantEntity,
    StrollerVariantSchema,
} from "@/mk2/schemas";
import { getDenormalizedEntities, getDenormalizedEntity } from "@/mk2/selectors";
import { interpolate } from "@/mk2/services/i18n";
import { Store } from "@/mk2/store/configureStore";
import {
    GALLERY,
    GALLERY_THUMBNAILS,
    SEO_TITLE_STROLLER_REVIEWS_stroller,
    STROLLER_SEO_GENERAL_DESC,
} from "@/mk/autogenerated/translation";

import styles from "./StrollerProductPhoto.mscss";

export const STROLLER_PRODUCT_PHOTO_HIDE_THUMBNAILS_COOKIE = "_strollerProductPhotoHideThumbnails";

export interface OwnProps {}

export interface StateProps {
    strollerSlug: string;
    strollerVariantId: number | null;
    photoId: number;
    loadingState: LoadingState;
    errorMessage: string;
    stroller: StrollerEntity;
    strollerPhotos: StrollerPhoto[];
    strollerPhoto: StrollerPhoto;
    strollerVariants: StrollerVariantEntity[];
    hideThumbnails: boolean;
    thumbsKey: string;
}

export interface DispatchProps {
    onLoad(slug: string, strollerVariantId: number | null, photoId: number);
    onCheckHideThumbnails();
    onToggleThumbnails();
}

export type RouteProps = PageRouteProps<{
    slug: string;
    variantId: string;
    photoId: string;
}>;

export type Props = OwnProps & StateProps & DispatchProps & RouteProps;

export default class StrollerProductPhotoPage extends Page<
    OwnProps & RouteProps,
    StateProps,
    DispatchProps
> {
    public mapStateToProps(): MapStateToPropsParam<StateProps, OwnProps & RouteProps, AppState> {
        return (state: AppState, ownProps: OwnProps & RouteProps): StateProps => {
            const slug = ownProps.match.params.slug;
            const photoId = Number(ownProps.match.params.photoId);
            const strollerVariantId = ownProps.match.params.variantId
                ? Number(ownProps.match.params.variantId)
                : null;

            const pagesState = getStrollerProductPhotoPagesState(state);
            const photosState = getStrollerProductPhotoState(state, slug, strollerVariantId);
            const stroller = getDenormalizedEntity<StrollerEntity>(
                state,
                StrollerSchema,
                photosState.strollerId,
            );
            const strollerVariants = getDenormalizedEntities<StrollerVariantEntity>(
                state,
                StrollerVariantSchema,
                photosState.strollerVariantIds,
            );
            const strollerPhotos = getDenormalizedEntities<StrollerPhoto>(
                state,
                PhotoDetailSchema,
                photosState.strollerPhotosIds,
            );
            const strollerPhoto = strollerPhotos.find((p) => p.id === photoId);

            const loadingState =
                photosState.loadingState != LoadingState.SUCCESS
                    ? photosState.loadingState
                    : strollerPhoto
                      ? LoadingState.SUCCESS
                      : LoadingState.FAILURE;
            const errorMessage =
                photosState.loadingState != LoadingState.SUCCESS
                    ? photosState.errorMessage
                    : strollerPhoto
                      ? null
                      : "NOT FOUND";

            const hideThumbnails = pagesState.hideThumbnails;
            const thumbsKey = pagesState.thumbsKey;

            return {
                strollerSlug: slug,
                strollerVariantId,
                photoId,
                loadingState,
                errorMessage,
                stroller,
                strollerVariants,
                strollerPhotos,
                strollerPhoto,
                hideThumbnails,
                thumbsKey,
            };
        };
    }

    public mapDispatchToProps(): MapDispatchToPropsObject<DispatchProps> {
        return {
            onLoad: strollerProductPhotoFetchTrigger,
            onCheckHideThumbnails: strollerProductPhotoCheckHideThumbs,
            onToggleThumbnails: strollerProductPhotoToggleThumbsTrigger,
        };
    }

    public getMetaTitle(props: Props): string {
        const { stroller } = props;
        return stroller
            ? interpolate(SEO_TITLE_STROLLER_REVIEWS_stroller, { stroller: stroller.name })
            : null;
    }

    public getMetaDescription(props: Props): string {
        const { stroller } = props;
        if (stroller) {
            return `${stroller.name} - ${STROLLER_SEO_GENERAL_DESC}`;
        }
    }

    public render(props: Props): false | JSX.Element {
        return <StrollerProductPhoto {...props} />;
    }

    public renderRight(props: Props): JSX.Element {
        return null;
    }

    public getHeaderRight(props: Props): React.ReactElement {
        const { onToggleThumbnails, strollerSlug, strollerVariantId } = props;
        return (
            <>
                <span className={styles.StrollerProductPhoto__faThHack}>
                    <FontAwesomeIcon icon={faTh} />
                </span>
                <Btn
                    type={BtnType.BlueText}
                    label={GALLERY_THUMBNAILS}
                    onClick={onToggleThumbnails}
                />
                <Btn
                    type={BtnType.BlueText}
                    icon={faTh}
                    label={GALLERY}
                    link={
                        strollerVariantId
                            ? url(strollersVariantPhotosGalleryUrl, {
                                  slug: strollerSlug,
                                  variantId: strollerVariantId,
                              })
                            : url(strollersPhotosGalleryUrl, { slug: strollerSlug })
                    }
                />
            </>
        );
    }

    public getRightContentSide(app: AppName, props: Props): RightContentSide {
        return RightContentSide.RightSide;
    }

    public hasMobileLogo(props: Props): boolean {
        return false;
    }

    public hasHeaderBackButton(props: Props): boolean {
        return true;
    }

    public getDefaultBackPath(props: Props): string {
        const { strollerSlug, strollerVariantId } = props;
        return strollerVariantId
            ? url(strollersVariantPhotosGalleryUrl, {
                  slug: strollerSlug,
                  variantId: strollerVariantId,
              })
            : url(strollersPhotosGalleryUrl, { slug: strollerSlug });
    }

    public getHalfpageProps(props: Props): HalfpageProps {
        // Disable ads when no image found (404)
        const image = props.strollerPhotos.find((i) => i.id === props.photoId);
        if (!image) {
            return null;
        }

        return {
            zone: "Strollers Index - Halfpage Sticky (Desktop)",
        };
    }

    public registerInStore(store: Store<AppState>) {
        store.injectReducer(
            "containers.strollers.strollersProductPhoto",
            strollersProductPhotoReducer,
        );
        store.injectSaga("containers.strollers.strollersProductPhoto", strollersProductPhotoSaga);
    }
}
